// src/components/DuplicateMergeModal.jsx
// Side-by-side comparison modal for resolving duplicate clients during import
import React, { useState, useMemo, useEffect } from "react";
import { X, AlertTriangle, Check, Users, ArrowRight, SkipForward, RefreshCw } from "lucide-react";

// Fields to compare with labels
const COMPARE_FIELDS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP Code' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'carrier', label: 'Carrier' },
  { key: 'plan', label: 'Plan' },
  { key: 'planType', label: 'Plan Type' },
  { key: 'effectiveDate', label: 'Effective Date' },
  { key: 'medicareId', label: 'Medicare ID' },
];

// Match type badge colors
const MATCH_TYPE_COLORS = {
  medicare_id: 'bg-purple-100 text-purple-800',
  ssn: 'bg-purple-100 text-purple-800',
  name_address: 'bg-blue-100 text-blue-800',
  name_phone: 'bg-green-100 text-green-800',
  phone_only: 'bg-yellow-100 text-yellow-800',
  name_dob: 'bg-orange-100 text-orange-800',
  name_only: 'bg-gray-100 text-gray-800',
  name_carrier: 'bg-indigo-100 text-indigo-800',
  default: 'bg-gray-100 text-gray-800'
};

export default function DuplicateMergeModal({
  isOpen,
  duplicate,       // { importIndex, matchType, matchTypeLabel, existingClient, importingRecord }
  onClose,
  onSave,          // (importIndex, mergedRecord) => void
  onSkip,          // (importIndex) => void - skip this record entirely
  currentIndex,    // Current position in duplicates array (1-based for display)
  totalDuplicates  // Total number of duplicates to resolve
}) {
  // Track which source to use for each field: 'existing' | 'importing'
  const [fieldSelections, setFieldSelections] = useState({});

  // Reset selections when duplicate changes
  useEffect(() => {
    if (duplicate) {
      // Default: prefer importing values where they exist, fall back to existing
      const initial = {};
      COMPARE_FIELDS.forEach(({ key }) => {
        const importVal = duplicate.importingRecord?.[key];
        const existVal = duplicate.existingClient?.[key];
        // If importing has a value, use it; otherwise use existing
        initial[key] = importVal ? 'importing' : 'existing';
      });
      setFieldSelections(initial);
    }
  }, [duplicate]);

  // Build the merged record based on selections
  const mergedRecord = useMemo(() => {
    if (!duplicate) return null;

    const merged = { clientId: duplicate.existingClient?.id };
    COMPARE_FIELDS.forEach(({ key }) => {
      const source = fieldSelections[key] || 'importing';
      merged[key] = source === 'existing'
        ? duplicate.existingClient?.[key]
        : duplicate.importingRecord?.[key];
    });
    return merged;
  }, [duplicate, fieldSelections]);

  // Quick actions
  const useAllExisting = () => {
    const selections = {};
    COMPARE_FIELDS.forEach(({ key }) => {
      selections[key] = 'existing';
    });
    setFieldSelections(selections);
  };

  const useAllImporting = () => {
    const selections = {};
    COMPARE_FIELDS.forEach(({ key }) => {
      selections[key] = 'importing';
    });
    setFieldSelections(selections);
  };

  const toggleField = (key, source) => {
    setFieldSelections(prev => ({ ...prev, [key]: source }));
  };

  const handleSave = () => {
    if (duplicate && mergedRecord) {
      onSave(duplicate.importIndex, mergedRecord);
    }
  };

  const handleSkip = () => {
    if (duplicate) {
      onSkip(duplicate.importIndex);
    }
  };

  if (!isOpen || !duplicate) return null;

  const matchColor = MATCH_TYPE_COLORS[duplicate.matchType] || MATCH_TYPE_COLORS.default;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-amber-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Users className="text-amber-600" size={22} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                Potential Duplicate Found
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${matchColor}`}>
                  {duplicate.matchTypeLabel}
                </span>
              </h3>
              <p className="text-sm text-slate-600">
                {currentIndex && totalDuplicates
                  ? `Reviewing ${currentIndex} of ${totalDuplicates} duplicates`
                  : 'Choose which values to keep for each field'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-amber-100 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-3 shrink-0">
          <span className="text-sm text-slate-600 font-medium">Quick Actions:</span>
          <button
            onClick={useAllExisting}
            className="px-3 py-1.5 text-sm rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Use All Existing
          </button>
          <button
            onClick={useAllImporting}
            className="px-3 py-1.5 text-sm rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition flex items-center gap-1"
          >
            <ArrowRight size={14} />
            Use All Importing
          </button>
        </div>

        {/* Field Comparison Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-[200px_1fr_1fr] gap-2">
            {/* Header Row */}
            <div className="font-semibold text-slate-700 text-sm pb-2 border-b">Field</div>
            <div className="font-semibold text-blue-700 text-sm pb-2 border-b flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              Existing Record
            </div>
            <div className="font-semibold text-green-700 text-sm pb-2 border-b flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              Importing Record
            </div>

            {/* Field Rows */}
            {COMPARE_FIELDS.map(({ key, label }) => {
              const existingVal = duplicate.existingClient?.[key] || '';
              const importingVal = duplicate.importingRecord?.[key] || '';
              const selected = fieldSelections[key] || 'importing';
              const isDifferent = existingVal !== importingVal;

              return (
                <React.Fragment key={key}>
                  {/* Field Label */}
                  <div className="py-2 text-sm text-slate-700 font-medium flex items-center">
                    {label}
                    {isDifferent && existingVal && importingVal && (
                      <AlertTriangle size={14} className="ml-2 text-amber-500" title="Values differ" />
                    )}
                  </div>

                  {/* Existing Value */}
                  <button
                    onClick={() => toggleField(key, 'existing')}
                    className={`py-2 px-3 text-sm text-left rounded-lg border-2 transition ${
                      selected === 'existing'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={existingVal ? 'text-slate-800' : 'text-slate-400 italic'}>
                        {existingVal || '(empty)'}
                      </span>
                      {selected === 'existing' && (
                        <Check size={16} className="text-blue-600 shrink-0 ml-2" />
                      )}
                    </div>
                  </button>

                  {/* Importing Value */}
                  <button
                    onClick={() => toggleField(key, 'importing')}
                    className={`py-2 px-3 text-sm text-left rounded-lg border-2 transition ${
                      selected === 'importing'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={importingVal ? 'text-slate-800' : 'text-slate-400 italic'}>
                        {importingVal || '(empty)'}
                      </span>
                      {selected === 'importing' && (
                        <Check size={16} className="text-green-600 shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between shrink-0">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
          >
            <SkipForward size={16} />
            Skip This Record
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition font-medium flex items-center gap-2"
            >
              <Check size={16} />
              Save Merged Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
